import * as React from 'react'
import * as jotai from 'jotai'

import type { ApiResponse } from '@owl-nest/api-client'
import * as api from '@owl-nest/api-client/next'
import * as hooks from '@owl-nest/hooks'

export async function createAccount(userId: number, payload: api.CreateAccount): Promise<ApiResponse<api.Account>> {
  const response = await api.client('POST /users/:id/accounts', {
    urlParams: { id: String(userId) },
    body: payload,
  })

  return response.next((success) => success.body)
}

export async function updateAccount(
  accountId: number,
  payload: api.UpdateAccount,
  params: URLSearchParams,
): Promise<ApiResponse<api.Account>> {
  const response = await api.client('PATCH /accounts/:id', {
    urlParams: { id: String(accountId) },
    body: payload,
    getParams: params,
  })

  return response.next((success) => success.body)
}

export async function deleteAccount(accountId: number): Promise<ApiResponse<void>> {
  const response = await api.client('DELETE /accounts/:id', {
    urlParams: { id: String(accountId) },
  })

  return response.next((success) => success.body)
}

export async function getAccount(accountId: number, params: URLSearchParams): Promise<ApiResponse<api.Account>> {
  const response = await api.client('GET /accounts/:id', {
    urlParams: { id: String(accountId) },
    getParams: params,
  })

  return response.next((success) => ({ ...success.body, account: success.body }))
}

export const ACCOUNT_CACHE = jotai.atom<hooks.Cache<api.Account>>({})

export type UseAccount = {
  response: hooks.Response<api.Account>
  query: () => void
  update: (payload: api.UpdateAccount) => Promise<ApiResponse<api.Account> | undefined>
  remove: () => Promise<ApiResponse<unknown> | undefined>
}

export function useAccount(accountId?: number, lang?: string): UseAccount {
  const sharedQuery = hooks.useSharedQuery(
    async (accountId: number, lang?: string) => {
      const searchParams = new URLSearchParams()
      if (lang) {
        searchParams.append('lang', lang)
      }

      return getAccount(accountId, searchParams)
    },
    {
      atom: ACCOUNT_CACHE,
      id,
    },
  )

  hooks.useFetchOnMount(
    {
      fetchOnMount: true,
      getResponse,
      query,
    },
    id(accountId, lang),
  )

  return {
    query,
    update,
    remove,
    response: getResponse(),
  }

  function query(): Promise<void> | undefined {
    if (accountId) {
      return sharedQuery.query(accountId, lang)
    }
  }

  async function remove(): Promise<ApiResponse<unknown> | undefined> {
    if (!accountId) {
      return
    }

    return sharedQuery.remove([accountId, lang], async () => await deleteAccount(accountId))
  }
  async function update(payload: api.UpdateAccount): Promise<ApiResponse<api.Account> | undefined> {
    if (!accountId) {
      return
    }
    const searchParams = new URLSearchParams()
    if (lang) {
      searchParams.append('lang', lang)
    }

    return sharedQuery.patch([accountId, lang], async () => await updateAccount(accountId, payload, searchParams))
  }

  function id(accountId?: number, lang?: string): string {
    return JSON.stringify([accountId, lang])
  }

  function getResponse(): hooks.Response<api.Account> {
    if (accountId === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }

    const response = sharedQuery.getCurrentResponse([accountId, lang])

    if (response === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }

    return response
  }
}

// Get rid of the API `null` typing and enforce empty arrays instead
export type AccountPage = { accounts: api.Account[] } & Omit<api.AccountList, 'accounts'>

export type GetPageParams = {
  limit?: string
  currency?: string
}

export async function getPage(userId: number, params: URLSearchParams): Promise<ApiResponse<AccountPage>> {
  const response = await api.client('GET /users/:id/accounts', {
    getParams: params,
    urlParams: { id: String(userId) },
    withToken: true,
  })

  return response.next((success) => ({ ...success.body, accounts: success.body.accounts ?? [] }))
}

export const ACCOUNT_PAGE_CACHE = jotai.atom<hooks.Cache<AccountPage[]>>({})

export type UseAccountPage = {
  response: hooks.Response<AccountPage[]>
  query: () => Promise<void>
  queryNext: () => Promise<void>
}

type UseAccountPageConfig = {
  params?: GetPageParams
  fetchOnMount?: boolean | 'force'
}

export function useAccountPage(
  userId: number | undefined,
  { params = {}, fetchOnMount = false }: UseAccountPageConfig = {},
): UseAccountPage {
  const sharedQuery = hooks.useSharedQuery(
    async (userId: number, params: GetPageParams) => {
      const response = await getPage(userId, toURLSearchParams(params))
      return response.next((success) => [success])
    },
    {
      atom: ACCOUNT_PAGE_CACHE,
      id,
    },
  )

  hooks.useFetchOnMount(
    {
      fetchOnMount,
      getResponse,
      query,
    },
    id(userId, params),
  )

  return { response: getResponse(), query, queryNext }

  async function query(): Promise<void> {
    if (userId) {
      return await sharedQuery.query(userId, params)
    }
  }

  function id(userId: number | undefined, params: GetPageParams): string {
    return JSON.stringify([userId, params.currency, params.limit])
  }

  function getResponse(): hooks.Response<AccountPage[]> {
    if (userId === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }

    const response = sharedQuery.getCurrentResponse([userId, params])

    if (response === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }

    return response
  }

  async function queryNext(): Promise<void> {
    if (userId) {
      const response = getResponse()
      const metaNext =
        response?.data !== undefined ? response.data[response.data.length - 1].meta?.next ?? undefined : undefined
      const nextSearchParams = new URLSearchParams(metaNext)

      if (metaNext === undefined) {
        console.warn(`[services/account] queryNext(), next don't exist`)
        return
      }

      sharedQuery.patch([userId, params], async (response) => {
        const getPageResponse = await getPage(userId, nextSearchParams)

        return getPageResponse.next((success) => {
          let pages = []
          if (response.data !== undefined) {
            pages = [...response.data, success]
          } else {
            pages = [success]
          }

          return pages
        })
      })
    }
  }
}

function toURLSearchParams(params: GetPageParams): URLSearchParams {
  const searchParams = new URLSearchParams()

  if (params === undefined) {
    return searchParams
  }

  if (params.currency) {
    searchParams.set('currency', String(params.currency))
  }

  if (params.limit) {
    searchParams.set('limit', String(params.limit))
  }

  return searchParams
}
